<script setup>
import { ref } from 'vue'

const props = defineProps({
  visible: Boolean,
  closable: Boolean,
})
const emit = defineEmits(['update:visible', 'confirm'])

const tempName = ref('')

const submit = () => {
  if (!tempName.value.trim()) return
  emit('confirm', tempName.value.trim())
  tempName.value = ''
}

const close = () => {
  if (!props.closable) return
  emit('update:visible', false)
}
</script>

<template>
  <Transition name="overlay" :duration="{ enter: 350, leave: 300 }">
    <div v-if="visible" class="overlay">
      <div class="backdrop" @click="close" />
      <Transition name="nombre-sheet">
        <div v-if="visible" class="nombre-sheet">
          <div class="drag-handle" />
          <h2 class="sheet-title">¿Cómo te llamás?</h2>
          <p class="sheet-sub">Ingresá tu nombre para identificar el trabajo que registres.</p>
          <input
            v-model="tempName"
            type="text"
            placeholder="Tu nombre"
            class="name-input"
            enterkeyhint="done"
            @keyup.enter="submit"
          />
          <button class="cta-btn" :disabled="!tempName.trim()" @click="submit">
            Continuar
          </button>
          <button v-if="closable" class="cancel-btn" @click="close">
            Cancelar
          </button>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
}

.backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.nombre-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-grouped);
  border-radius: 20px 20px 0 0;
  padding: 8px 20px;
  padding-bottom: max(env(safe-area-inset-bottom, 20px), 20px);
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
}

.drag-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--label-rgb), 0.25);
  margin: 6px auto 20px;
}

.sheet-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--label-primary);
  margin: 0 0 8px;
  letter-spacing: -0.3px;
}

.sheet-sub {
  font-size: 15px;
  color: rgba(var(--label-rgb), 0.6);
  margin: 0 0 20px;
  line-height: 1.45;
}

.name-input {
  display: block;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--bg-primary);
  border: none;
  font-size: 17px;
  color: var(--label-primary);
  outline: none;
  margin-bottom: 14px;
  box-sizing: border-box;
  -webkit-appearance: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.name-input::placeholder {
  color: rgba(var(--label-rgb), 0.35);
}

.cta-btn {
  display: block;
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: var(--accent);
  color: #fff;
  border: none;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, transform 0.08s;
  -webkit-tap-highlight-color: transparent;
  letter-spacing: -0.2px;
  margin-bottom: 4px;
}

.cta-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cta-btn:not(:disabled):active {
  background: var(--accent-pressed);
  transform: scale(0.98);
}

.cancel-btn {
  display: block;
  width: 100%;
  padding: 13px;
  background: none;
  border: none;
  font-size: 15px;
  color: rgba(var(--label-rgb), 0.55);
  cursor: pointer;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
}

/* Transición del overlay (backdrop) */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Transición del sheet */
.nombre-sheet-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}
.nombre-sheet-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}
.nombre-sheet-enter-from,
.nombre-sheet-leave-to {
  transform: translateY(100%);
}
</style>
